import Typography from '@mui/material/Typography'
import { differenceInHours, isAfter, isEqual, isPast, isSameDay, isThisYear, parseISO, subSeconds } from 'date-fns'
import { relativeDate, zonedDate } from 'global/helpers/dates'
import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { type LegData, type StopData, WorkflowType } from './TripDetails.types'

export interface StatusDateMessageProps {
  leg: LegData
  stop: StopData
}

const StatusDateMessage = ({
  leg,
  stop,
}: StatusDateMessageProps): ReactElement | null => {
  const { t } = useTranslation()

  if (!stop.earlyDateTime || !stop.lateDateTime) {
    return null
  }

  const parsedEarlyDateTime = parseISO(stop.earlyDateTime)
  const parsedLateDateTime = parseISO(stop.lateDateTime)
  const latestStatus = stop.statuses.length > 0 ? stop.statuses[0] : undefined

  // HACK: createdAt should be returned in proper TZ from the backend, and not adjusted here
  // currently it's always marked as UTC, but createdAtOffset stores actual offset, e.g.:
  // with createdAt = 2024-01-01 12:00:00Z and createdAtOffset = 3600, proper createdAt value should be 2024-01-01 12:00:00+01:00
  // it's a limitation of how postgres stores dates, needs some BE work
  const parsedLatestStatus = latestStatus?.createdAt ? subSeconds(parseISO(latestStatus.createdAt), latestStatus.createdAtOffset ?? 0) : undefined
  const isDelayed = (!parsedLatestStatus && isPast(parsedLateDateTime)) || (parsedLatestStatus && isAfter(parsedLatestStatus, parsedLateDateTime))
  const isUpcoming = !parsedLatestStatus && !isDelayed && Math.abs(differenceInHours(parsedEarlyDateTime, new Date())) <= 8

  const getDateColor = () => {
    if (isUpcoming) {
      return 'warning'
    } else if (isDelayed) {
      return 'error'
    }
    return 'primary.light'
  }

  const getDateMessage = (): string | null => {
    const fullDateFormat = isThisYear(parsedEarlyDateTime) ? 'full_no_year' : 'full'
    const mediumDateFormat = isThisYear(parsedEarlyDateTime) ? 'medium_no_year' : 'medium'
    if (stop.type === 'CUS') {
      if (latestStatus) {
        return t('screens.cs.trip_details.card.executed_on', {
          date: zonedDate(latestStatus.createdAt, fullDateFormat),
        })
      }
      return null
    }
    if (latestStatus && parsedLatestStatus) {
      // show delayed message only for OK and non-SCD statuses
      if (isDelayed && ['0500', '0502', '3000', '3002'].includes(latestStatus.statusCode) && leg.workflowType !== WorkflowType.SECURE_CLOUD_DEPLOYMENT) {
        return t('screens.cs.trip_details.card.executed_on_delayed', {
          date: zonedDate(latestStatus.createdAt, fullDateFormat),
          delay: relativeDate(parsedLatestStatus, parsedLateDateTime),
        })
      }
      return t('screens.cs.trip_details.card.executed_on', {
        date: zonedDate(latestStatus.createdAt, fullDateFormat),
      })
    } else if (isEqual(parsedEarlyDateTime, parsedLateDateTime)) {
      return t('screens.cs.trip_details.card.expected_on_same_time', {
        date: zonedDate(stop.earlyDateTime, fullDateFormat),
      })
    } else if (isSameDay(parsedEarlyDateTime, parsedLateDateTime)) {
      return t('screens.cs.trip_details.card.expected_on_same_day', {
        date: zonedDate(stop.earlyDateTime, mediumDateFormat),
        hour1: zonedDate(stop.earlyDateTime, 'time'),
        hour2: zonedDate(stop.lateDateTime, 'time'),
      })
    } else {
      return t('screens.cs.trip_details.card.expected_on', {
        date1: zonedDate(stop.earlyDateTime, fullDateFormat),
        date2: zonedDate(stop.lateDateTime, fullDateFormat),
      })
    }
  }

  const dateMessage = getDateMessage()
  if (!dateMessage) {
    return null
  }

  return (
    <Typography variant="p3" color={getDateColor()} component="p">
      {dateMessage}
    </Typography>
  )
}

export default StatusDateMessage
